import { useRef, useState, type ComponentProps, type ReactNode } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { cn } from '../../lib/utils';
import { FieldLabelRow } from './field-label-row';

export default function CurrencyField({
	label,
	className,
	inputClassName,
	optional,
	info,
	decimalsLimit = 2,
	allowNegativeValue = false,
	suffix = '€',
	prefix,
	placeholder,
	...props
}: Omit<ComponentProps<typeof CurrencyInput>, 'value' | 'onValueChange' | 'onBlur' | 'id' | 'name'> & {
	label: string;
	inputClassName?: string;
	optional?: boolean;
	info?: ReactNode;
}) {
	const field = useFieldContext<number | null | undefined>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);

	const showDash = isReadOnly && (field.state.value == null || field.state.value === 0);

	// `Number("100.")` strips the dot, which made decimal typing impossible.
	const [displayValue, setDisplayValue] = useState(() => (field.state.value != null ? String(field.state.value) : ''));
	const syncedRef = useRef(field.state.value);
	if (!Object.is(field.state.value, syncedRef.current)) {
		syncedRef.current = field.state.value;
		setDisplayValue(field.state.value != null ? String(field.state.value) : '');
	}

	return (
		<div className={className}>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={info}
					/>
				)}
				<CurrencyInput
					placeholder={placeholder}
					id={field.name}
					name={field.name}
					aria-invalid={hasFieldError(field.state.meta)}
					value={showDash ? '-' : displayValue}
					onBlur={() => {
						setDisplayValue(field.state.value != null ? String(field.state.value) : '');
						field.handleBlur();
					}}
					onValueChange={
						isReadOnly
							? undefined
							: (_value, _name, values) => {
									setDisplayValue(_value ?? '');
									const num = values?.float ?? null;
									field.handleChange(num);
									syncedRef.current = num;
								}
					}
					decimalsLimit={decimalsLimit}
					decimalScale={isReadOnly ? 0 : props.decimalScale}
					allowNegativeValue={allowNegativeValue}
					suffix={suffix}
					prefix={prefix}
					readOnly={isReadOnly}
					tabIndex={isReadOnly ? -1 : undefined}
					className={cn(
						'dark:bg-input/30 border-input focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 disabled:bg-input/50 dark:disabled:bg-input/80 h-8 rounded-lg border bg-transparent px-2.5 py-1 text-base transition-[color,border-color,padding,box-shadow] file:h-6 file:text-sm file:font-medium aria-invalid:ring-3 md:text-sm file:text-foreground placeholder:text-muted-foreground w-full min-w-0 outline-none file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
						inputClassName,
						isReadOnly && 'border-transparent px-0 focus-visible:ring-0 focus-visible:border-transparent',
					)}
					{...props}
				/>
			</div>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
